import React from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Slider,
  Button,
  ButtonGroup,
  Stack,
  Chip,
  FormHelperText,
  Grid2
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { CastForEducationOutlined, SchoolOutlined } from "@mui/icons-material";

const scaleLabels = ["None", "Low", "Medium", "High"];
const valueToLabel = (val) => scaleLabels[val] ?? String(val);

const initialValues = {
  study_hours: 2,
  sleep_hours: 7.5,
  effort_score: 5,
  tutor_sessions: 0,
  parental_help: 1,
  sports_hours: 0,
  peer_influence: 1,
};

const validationSchema = Yup.object({
  study_hours: Yup.number().min(0).max(12).required("Required"),
  sleep_hours: Yup.number().min(0).max(12).required("Required"),
  effort_score: Yup.number().integer().min(0).max(10).required("Required"),
  tutor_sessions: Yup.number().integer().min(0).max(10).required("Required"),
  parental_help: Yup.number().integer().oneOf([0, 1, 2, 3]).required("Required"),
  sports_hours: Yup.number().integer().min(0).max(20).required("Required"),
  peer_influence: Yup.number().integer().oneOf([0, 1, 2, 3]).required("Required"),
});

export default function StudentPreAssessmentForm({ onSubmit, student }) {
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(vals, { setSubmitting }) => {
        const payload = {
          ...vals,
          effort_score: Math.round(vals.effort_score),
          tutor_sessions: Math.round(vals.tutor_sessions),
          sports_hours: Math.round(vals.sports_hours),
          study_hours: Math.round(vals.study_hours),
        };
        onSubmit?.(payload);
        setSubmitting(false);
      }}
    >
      {({ values, errors, touched, handleSubmit, setFieldValue, isSubmitting, isValid, resetForm }) => (
        <Box component="form" onSubmit={handleSubmit}>
          <Card className="rounded-2xl shadow-lg">
            <CardHeader
              title={
                <Box sx={{display: 'flex', alignContent: 'center', justifyContent: 'left', alignItems: 'center'}}>
                  <SchoolOutlined />
                  <Typography variant="h5" fontWeight={'bold'}>Student Pre‑Assessment</Typography>
                </Box>
              }
              subheader={<>
                <Typography variant="subtitle1">Please answer honesly, assessment will start once submitted.</Typography>
                {student && (
                  <Typography color="text.secondary" variant="subtitle1" gutterBottom>
                    Student: {student.first_name} {student.middle_name} {student.last_name}
                  </Typography>
                )}
              </>}
            />
            <Divider />
            <CardContent>
              <Stack spacing={3} sx={{p: 3}}>

              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1">How many hours did you study (hr / night) ? </Typography>
                  <Chip label={`${values.study_hours.toFixed(1)} hrs`} size="small" />
                </Stack>
                <Slider
                  value={values.study_hours}
                  onChange={(_, val) => setFieldValue("study_hours", val)}
                  min={0}
                  max={24}
                  color="secondary"
                  step={0.5}
                  valueLabelDisplay="auto"
                  marks={[{ value: 0, label: "0" }, { value: 6, label: "6" }, { value: 8, label: "8" }, { value: 12, label: "12" }, { value: 18, label: "18" }, { value: 24, label: "24" } ]}
                />
                {touched.study_hours && errors.study_hours && (
                  <FormHelperText error>{errors.study_hours}</FormHelperText>
                )}
                </Stack>

                {/* Sleep Hours */}
                <Stack spacing={1}>


                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1">How many hours do you sleep ? (hrs / night) </Typography>
                    <Chip label={`${values.sleep_hours.toFixed(1)} hrs`} size="small" />
                  </Stack>
                  <Slider
                    value={values.sleep_hours}
                    onChange={(_, val) => setFieldValue("sleep_hours", val)}
                    min={0}
                    max={12}
                    color="secondary"
                    step={0.5}
                    valueLabelDisplay="auto"
                    marks={[{ value: 0, label: "0" }, { value: 6, label: "6" }, { value: 8, label: "8" }, { value: 12, label: "12" }]}
                  />
                  {touched.sleep_hours && errors.sleep_hours && (
                    <FormHelperText error>{errors.sleep_hours}</FormHelperText>
                  )}
                </Stack>

                {/* Effort Score */}
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1">How much effort preparing for this assessment ? (0–10)</Typography>
                    <Chip label={Math.round(values.effort_score)} size="small" />
                  </Stack>
                  <Slider
                    value={values.effort_score}
                    onChange={(_, val) => setFieldValue("effort_score", Math.round(val))}
                    min={0}
                    max={10}
                    color="secondary"
                    step={1}
                    valueLabelDisplay="auto"
                    marks={[{ value: 0, label: "0" }, { value: 5, label: "5" }, { value: 10, label: "10" }]}
                  />
                  {touched.effort_score && errors.effort_score && (
                    <FormHelperText error>{errors.effort_score}</FormHelperText>
                  )}
                </Stack>

                {/* Tutor Sessions */}
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1">How often do you get tutoring ? (per week)</Typography>
                    <Chip label={`${values.tutor_sessions}`} size="small" />
                  </Stack>
                  <Slider
                    value={values.tutor_sessions}
                    onChange={(_, val) => setFieldValue("tutor_sessions", Math.round(val))}
                    min={0}
                    color="secondary"
                    max={10}
                    step={1}
                    valueLabelDisplay="auto"
                    marks={[{ value: 0, label: "0" }, { value: 5, label: "5" }, { value: 10, label: "10" }]}
                  />
                  {touched.tutor_sessions && errors.tutor_sessions && (
                    <FormHelperText error>{errors.tutor_sessions}</FormHelperText>
                  )}
                </Stack>

                {/* Sports Hours */}
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1">Sports / Physical Activity ? (hrs per week)</Typography>
                    <Chip label={`${values.sports_hours} hrs`} size="small" />
                  </Stack>
                  <Slider
                    value={values.sports_hours}
                    onChange={(_, val) => setFieldValue("sports_hours", Math.round(val))}
                    min={0}
                    max={20}
                    color="secondary"
                    step={1}
                    valueLabelDisplay="auto"
                    marks={[{ value: 0, label: "0" }, { value: 10, label: "10" }, { value: 20, label: "20" }]}
                  />
                  {touched.sports_hours && errors.sports_hours && (
                    <FormHelperText error>{errors.sports_hours}</FormHelperText>
                  )}
                </Stack>
                
                {/* Parental Help & Peer Influence */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={4}>
                  <Stack flex={1} spacing={1}>
                    <Typography variant="subtitle1">Parental Help</Typography>
                    <Typography variant="body2">How often do your get help from parents/guardiant?</Typography>

                    <ButtonGroup
                      fullWidth
                      variant="outlined"
                      size="small"
                    >
                      {[0, 1, 2, 3].map((v) => (
                        <Button
                          key={v}
                          onClick={() => setFieldValue("parental_help", v)}
                          variant={values.parental_help === v ? "contained" : "outlined"}
                        >
                          {valueToLabel(v)}
                        </Button>
                      ))}
                    </ButtonGroup>
                    {touched.parental_help && errors.parental_help && (
                      <FormHelperText error>{errors.parental_help}</FormHelperText>
                    )}
                  </Stack>

                  <Stack flex={1} spacing={1}>
                    <Typography variant="subtitle1">Peer Influence</Typography>
                    <Typography variant="body2">How often to your friends, peers help out?</Typography>
                    <ButtonGroup
                      fullWidth
                      variant="outlined"
                      size="small"
                    >
                      {[0, 1, 2, 3].map((v) => (
                        <Button
                          key={v}
                          onClick={() => setFieldValue("peer_influence", v)}
                          variant={values.peer_influence === v ? "contained" : "outlined"}
                        >
                          {valueToLabel(v)}
                        </Button>
                      ))}
                    </ButtonGroup>
                    {touched.peer_influence && errors.peer_influence && (
                      <FormHelperText error>{errors.peer_influence}</FormHelperText>
                    )}
                  </Stack>
                </Stack>

                {/* Actions */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="flex-end">
                  <Button onClick={() => resetForm()} variant="text" type="button">
                    Reset
                  </Button>
                  <Button
                    color="secondary"
                   type="submit" variant="contained" disabled={isSubmitting || !isValid}>
                    Submit
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}
    </Formik>
  );
}